import {
  createProjectInput,
  PROJECT_STATUSES,
  type ProjectDetail,
  type ProjectStatus,
  updateProjectInput,
} from '@fos/shared/contracts/projects';
import { PROJECT_STATUS_LABELS } from '@fos/shared/labels';
import { Plus } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { useActiveSession } from '../../../lib/auth';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Combobox, type ComboboxOption } from '../../../ui/Combobox';
import { DateInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Select, type SelectOption } from '../../../ui/Select';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE } from '../../mobile';
import { hoursInputValue, parseHoursInput } from '../project-format';
import { ClientDot } from './ProjectBits';

const NONE = '__none__';

type FieldKey =
  | 'name'
  | 'clientId'
  | 'leadUserId'
  | 'status'
  | 'startDate'
  | 'endDate'
  | 'hoursBudgetSeconds'
  | 'goal'
  | 'scopeMd'
  | 'repoUrl'
  | 'figmaUrl';
type FieldErrors = Partial<Record<FieldKey, string>>;

const FIELD_KEYS: readonly FieldKey[] = [
  'name',
  'clientId',
  'leadUserId',
  'status',
  'startDate',
  'endDate',
  'hoursBudgetSeconds',
  'goal',
  'scopeMd',
  'repoUrl',
  'figmaUrl',
];

function isFieldKey(value: string): value is FieldKey {
  return (FIELD_KEYS as readonly string[]).includes(value);
}

interface FormState {
  name: string;
  clientId: string | null;
  isInternal: boolean;
  status: ProjectStatus;
  leadUserId: string;
  startDate: string;
  endDate: string;
  budget: string;
  billableDefault: boolean;
  goal: string;
  scopeMd: string;
  repoUrl: string;
  figmaUrl: string;
}

function initialState(project: ProjectDetail | undefined, defaults: { clientId?: string | null; userId: string }) {
  if (project) {
    return {
      name: project.name,
      clientId: project.clientId,
      isInternal: project.isInternal,
      status: project.status,
      leadUserId: project.lead?.userId ?? NONE,
      startDate: project.startDate ?? '',
      endDate: project.endDate ?? '',
      budget: hoursInputValue(project.hoursBudgetSeconds),
      billableDefault: project.billableDefault,
      goal: project.goal ?? '',
      scopeMd: project.scopeMd ?? '',
      repoUrl: project.repoUrl ?? '',
      figmaUrl: project.figmaUrl ?? '',
    } satisfies FormState;
  }
  return {
    name: '',
    clientId: defaults.clientId ?? null,
    isInternal: false,
    status: 'active',
    leadUserId: defaults.userId,
    startDate: '',
    endDate: '',
    budget: '',
    billableDefault: true,
    goal: '',
    scopeMd: '',
    repoUrl: '',
    figmaUrl: '',
  } satisfies FormState;
}

const orNull = (v: string) => (v.trim() ? v.trim() : null);

/**
 * Nowy projekt / edycja projektu (modal 560 px). Klient opcjonalny (lista `projects.clientOptions`: klienci,
 * dla których osoba może założyć projekt); „Projekt wewnętrzny” = bez klienta, czas domyślnie nierozliczany.
 * Błędy serwera trafiają do pól (VALIDATION_ERROR) albo do komunikatu (np. CONFLICT przy zmianie klienta).
 */
export function ProjectFormDialog({
  open,
  onOpenChange,
  project,
  defaultClientId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** edycja istniejącego projektu; brak = nowy projekt */
  project?: ProjectDetail;
  /** nowy projekt z profilu klienta */
  defaultClientId?: string | null;
  onSaved?: (project: ProjectDetail) => void;
}) {
  const session = useActiveSession();
  const access = useAccess();
  const utils = trpc.useUtils();
  const editing = Boolean(project);

  const clientsQuery = trpc.projects.clientOptions.useQuery(undefined, { enabled: open });
  const teamQuery = trpc.team.listMembers.useQuery(undefined, { enabled: open });
  const create = trpc.projects.create.useMutation();
  const update = trpc.projects.update.useMutation();

  const [form, setForm] = useState<FormState>(() =>
    initialState(project, { clientId: defaultClientId, userId: session.user.id }),
  );
  // ustawienia rozliczeniowe (budżet, „domyślnie rozliczany”): edycja całego klienta (DECISIONS 2026-10-08 [P])
  const billingLocked = Boolean(project && !project.can.editBilling);
  const [billableTouched, setBillableTouched] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  // świeży formularz przy każdym otwarciu
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu
  useEffect(() => {
    if (!open) return;
    setForm(initialState(project, { clientId: defaultClientId, userId: session.user.id }));
    setBillableTouched(false);
    setShowMore(Boolean(project));
    setErrors({});
    setFormError(null);
  }, [open]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  // „Wybrani klienci” / „Tylko przypisani”: projekt bez klienta prowadzi autor (inaczej zniknąłby z listy)
  const limitedScope = access.role === 'user' && access.dataScope !== 'all';
  // „Wybrani klienci” nie zakładają projektów bez klienta (backend: FORBIDDEN), więc nie pokazujemy tej opcji
  const allowNoClient = editing || !limitedScope || access.dataScope === 'assigned';
  const noClient = form.isInternal || !form.clientId;
  const leadLocked = editing ? !project?.can.manageMembers : limitedScope && noClient;

  const clientOptions = useMemo<ComboboxOption[]>(() => {
    const list = (clientsQuery.data ?? []).map((c) => ({
      value: c.id,
      label: c.name,
      leading: <ClientDot client={c} />,
    }));
    if (project?.client && !list.some((o) => o.value === project.client?.id)) {
      list.unshift({
        value: project.client.id,
        label: project.client.name,
        leading: <ClientDot client={project.client} />,
      });
    }
    return allowNoClient
      ? [{ value: NONE, label: 'Bez klienta', leading: <ClientDot client={null} /> }, ...list]
      : list;
  }, [clientsQuery.data, project, allowNoClient]);

  const leadOptions = useMemo<SelectOption[]>(() => {
    const active = (teamQuery.data ?? []).filter((m) => m.status === 'active');
    const options: SelectOption[] = active.map((m) => ({
      value: m.userId,
      label: m.userId === session.user.id ? `${m.name} (Ty)` : m.name,
    }));
    if (project?.lead && !options.some((o) => o.value === project.lead?.userId)) {
      options.push({ value: project.lead.userId, label: project.lead.name });
    }
    if (!options.some((o) => o.value === session.user.id)) {
      options.unshift({ value: session.user.id, label: `${session.user.name} (Ty)` });
    }
    return [{ value: NONE, label: 'Bez prowadzącego' }, ...options];
  }, [teamQuery.data, project, session.user]);

  const statusOptions = PROJECT_STATUSES.map((s) => ({ value: s, label: PROJECT_STATUS_LABELS[s] }));

  const toggleInternal = (internal: boolean) => {
    setForm((f) => ({
      ...f,
      isInternal: internal,
      clientId: internal ? null : f.clientId,
      billableDefault: billableTouched ? f.billableDefault : !internal,
      leadUserId: internal && limitedScope && !editing ? session.user.id : f.leadUserId,
    }));
  };

  const applyIssues = (issues: Array<{ path: string; message: string }>): boolean => {
    const next: FieldErrors = {};
    for (const issue of issues) {
      const key = issue.path.split('.')[0] ?? '';
      if (isFieldKey(key) && !next[key]) next[key] = issue.message;
    }
    setErrors(next);
    return Object.keys(next).length > 0;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const hours = parseHoursInput(form.budget);
    if (hours === undefined) {
      setErrors({ hoursBudgetSeconds: 'Podaj liczbę godzin, np. 40 albo 12,5' });
      return;
    }
    if (!allowNoClient && !form.clientId) {
      setErrors({ clientId: 'Wybierz klienta z listy' });
      return;
    }
    const fields = {
      name: form.name,
      clientId: form.isInternal ? null : form.clientId,
      isInternal: form.isInternal,
      status: form.status,
      startDate: orNull(form.startDate),
      endDate: orNull(form.endDate),
      hoursBudgetSeconds: hours,
      billableDefault: form.billableDefault,
      goal: orNull(form.goal),
      scopeMd: orNull(form.scopeMd),
      repoUrl: orNull(form.repoUrl),
      figmaUrl: orNull(form.figmaUrl),
      leadUserId: form.leadUserId === NONE ? null : form.leadUserId,
    };

    try {
      let saved: ProjectDetail;
      if (project) {
        const input = { ...fields, id: project.id, version: project.version };
        const parsed = updateProjectInput.safeParse(input);
        if (!parsed.success) {
          applyIssues(parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })));
          return;
        }
        saved = await update.mutateAsync(input);
        toast.success('Zapisano zmiany projektu');
      } else {
        const parsed = createProjectInput.safeParse(fields);
        if (!parsed.success) {
          applyIssues(parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })));
          return;
        }
        saved = await create.mutateAsync(fields);
        toast.success('Projekt utworzony');
      }
      setErrors({});
      await utils.projects.invalidate();
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      if (appErrorCode(err) === 'VALIDATION_ERROR' && applyIssues(validationIssues(err))) return;
      setFormError(errorMessage(err));
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title={editing ? 'Edytuj projekt' : 'Nowy projekt'}
      className={FULLSCREEN_ON_PHONE}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="project-form" loading={pending}>
            {editing ? 'Zapisz' : 'Utwórz projekt'}
          </Button>
        </>
      }
    >
      <form
        id="project-form"
        ref={formRef}
        onSubmit={submit}
        noValidate
        className="flex flex-col gap-4"
        aria-label={editing ? 'Edycja projektu' : 'Nowy projekt'}
      >
        <Field label="Nazwa" error={errors.name}>
          <Input
            name="name"
            autoFocus={!editing}
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="np. Redesign strony"
          />
        </Field>

        <div className="flex flex-col gap-2">
          <Field label="Klient" error={errors.clientId}>
            <Combobox
              value={form.isInternal ? NONE : (form.clientId ?? (allowNoClient ? NONE : null))}
              onValueChange={(v) => set('clientId', v === NONE ? null : v)}
              options={clientOptions}
              placeholder={clientsQuery.isPending ? 'Wczytywanie…' : 'Wybierz klienta'}
              searchPlaceholder="Szukaj klienta…"
              emptyText={clientsQuery.isError ? 'Nie udało się wczytać klientów' : 'Brak klientów'}
              disabled={form.isInternal}
            />
          </Field>
          {allowNoClient && (
            <Checkbox
              checked={form.isInternal}
              onCheckedChange={toggleInternal}
              label="Projekt wewnętrzny (bez klienta)"
            />
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Prowadzi"
            error={errors.leadUserId}
            hint={leadLocked && !editing ? 'Projekt bez klienta prowadzisz Ty.' : undefined}
          >
            <Select
              value={form.leadUserId}
              onValueChange={(v) => set('leadUserId', v)}
              options={leadOptions}
              disabled={leadLocked}
            />
          </Field>
          <Field label="Status" error={errors.status}>
            <Select
              value={form.status}
              onValueChange={(v) => set('status', v as ProjectStatus)}
              options={statusOptions}
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Początek" error={errors.startDate}>
            <DateInput value={form.startDate} onValueChange={(v) => set('startDate', v)} />
          </Field>
          <Field label="Termin" error={errors.endDate}>
            <DateInput value={form.endDate} onValueChange={(v) => set('endDate', v)} />
          </Field>
        </div>

        <div className="grid items-start gap-4 sm:grid-cols-2">
          <Field
            label="Budżet godzin"
            error={errors.hoursBudgetSeconds}
            hint={billingLocked ? 'Zmienia osoba z dostępem do całego klienta' : 'Opcjonalnie, np. 40'}
          >
            <Input
              name="budget"
              mono
              inputMode="decimal"
              value={form.budget}
              onChange={(e) => set('budget', e.target.value)}
              placeholder="—"
              disabled={billingLocked}
            />
          </Field>
          <div className="flex h-10 items-center sm:mt-6">
            <Checkbox
              disabled={billingLocked}
              checked={form.billableDefault}
              onCheckedChange={(v) => {
                setBillableTouched(true);
                set('billableDefault', v);
              }}
              label="Czas domyślnie rozliczany"
            />
          </div>
        </div>

        {showMore ? (
          <>
            <Field label="Cel" error={errors.goal}>
              <Textarea rows={2} value={form.goal} onChange={(e) => set('goal', e.target.value)} />
            </Field>
            <Field label="Zakres" error={errors.scopeMd} hint="Możesz użyć Markdown: listy, nagłówki, linki.">
              <Textarea rows={5} value={form.scopeMd} onChange={(e) => set('scopeMd', e.target.value)} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Repozytorium" error={errors.repoUrl}>
                <Input
                  type="url"
                  value={form.repoUrl}
                  onChange={(e) => set('repoUrl', e.target.value)}
                  placeholder="https://github.com/…"
                />
              </Field>
              <Field label="Figma" error={errors.figmaUrl}>
                <Input
                  type="url"
                  value={form.figmaUrl}
                  onChange={(e) => set('figmaUrl', e.target.value)}
                  placeholder="https://www.figma.com/…"
                />
              </Field>
            </div>
          </>
        ) : (
          <div>
            <Button variant="ghost" size="s" icon={Plus} onClick={() => setShowMore(true)} className="-ml-2.5">
              Cel, zakres i linki
            </Button>
          </div>
        )}

        {formError && <FormMessage>{formError}</FormMessage>}
      </form>
    </Dialog>
  );
}
